/**
 * Product Card Swatches - Optimized with Event Delegation
 * Handles color swatch hover/click interactions for all product cards
 */

(function() {
  'use strict';

  // Guard: Prevent script from running multiple times if loaded in multiple sections
  if (window.cardProductSwatchesInitialized) {
    return;
  }
  window.cardProductSwatchesInitialized = true;

  // Shared mobile detection function
  const isMobile = () => {
    return window.innerWidth <= 749 || 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  };

  // Track initialized cards to avoid re-initialization
  const initializedCards = new WeakSet();
  
  // Store original product URLs for each card
  const originalProductUrls = new WeakMap();
  
  // Store the original default swatch link for each card (the one with select_border on load)
  const originalDefaultSwatchLinks = new WeakMap();

  function elevateSwatchLayer(productCard, elevated) {
    const layer = productCard.querySelector(
      '.product-card__media .product-card__swatch-layer'
    );
    if (!layer) {
      return;
    }
    layer.classList.toggle(
      'product-card__swatch-layer--elevated',
      Boolean(elevated)
    );
  }

  /**
   * Initialize a single product card's swatch functionality
   */
  function initializeCard(productCard) {
    // Skip if already initialized
    if (initializedCards.has(productCard)) {
      return;
    }

    const mediaContainer = productCard.querySelector('.product-card__media');
    if (!mediaContainer) return;

    const swatchLinks = productCard.querySelectorAll('.product-card__swatch-link[data-swatch-image-id]');
    if (swatchLinks.length === 0) return;

    // Store the original product URL from the first product card link
    const productCardLinks = productCard.querySelectorAll('.product-card__link[data-product-link]');
    if (productCardLinks.length > 0) {
      const originalUrl = productCardLinks[0].href;
      originalProductUrls.set(productCard, originalUrl);
    }
    
    // Store the original default swatch link (the one with select_border class on initial load)
    const originalDefaultSwatch = productCard.querySelector('.width_swatch.select_border .product-card__swatch-link');
    if (originalDefaultSwatch) {
      originalDefaultSwatchLinks.set(productCard, originalDefaultSwatch);
    }

    // Ensure media container has hoverable class for proper positioning
    if (!mediaContainer.classList.contains('product-card__media--hoverable')) {
      mediaContainer.classList.add('product-card__media--hoverable');
    }

    // Get all swatch images and hover image
    const swatchImages = mediaContainer.querySelectorAll('.product-card__swatch-image');
    const hoverImage = mediaContainer.querySelector('.hover-img');
    const productCardActions = productCard.querySelector('.product-card__actions');

    // Initialize swatch images positioning
    swatchImages.forEach(img => {
      img.style.position = 'absolute';
      img.style.display = 'block';
      img.style.inset = '0';
      img.style.zIndex = '0';
      img.style.opacity = '0';
      img.style.transition = 'opacity 0.3s ease';
      img.classList.remove('hidden');
    });

    elevateSwatchLayer(productCard, false);

    // Fix hover-img visibility with MutationObserver
    if (hoverImage && productCardActions) {
      const observer = new MutationObserver(() => {
        if (productCardActions.classList.contains('is--hovering')) {
          hoverImage.style.zIndex = '1';
          hoverImage.style.opacity = '1';
        } else {
          hoverImage.style.zIndex = '';
          hoverImage.style.opacity = '';
        }
      });

      observer.observe(productCardActions, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Mark as initialized
    initializedCards.add(productCard);
  }

  /**
   * Show a swatch image and optionally update product links
   */
  function showSwatchImage(productCard, swatchImage, productUrl) {
    const mediaContainer = productCard.querySelector('.product-card__media');
    if (!mediaContainer) return;

    const swatchImages = mediaContainer.querySelectorAll('.product-card__swatch-image');
    const hoverImage = mediaContainer.querySelector('.hover-img');
    const productCardLinks = productCard.querySelectorAll('.product-card__link[data-product-link]');

    // Hide all swatch images first
    swatchImages.forEach(img => {
      img.style.opacity = '0';
      img.style.zIndex = '0';
    });

    // Show the selected swatch image
    elevateSwatchLayer(productCard, true);
    swatchImage.style.opacity = '1';
    swatchImage.style.zIndex = '4';

    // Hide hover-img when showing swatch image
    if (hoverImage) {
      hoverImage.style.opacity = '0';
      hoverImage.style.zIndex = '0';
    }

    // Update all product card links to point to the selected swatch's product URL
    if (productUrl && productCardLinks.length > 0) {
      productCardLinks.forEach(link => {
        link.href = productUrl;
      });
    }
  }

  /**
   * Hide swatch image and reset to default
   */
  function hideSwatchImage(productCard, swatchImage) {
    const hoverImage = productCard.querySelector('.product-card__media .hover-img');

    swatchImage.style.opacity = '0';
    swatchImage.style.zIndex = '0';

    elevateSwatchLayer(productCard, false);

    // Reset hover-img so CSS can control it again
    if (hoverImage) {
      hoverImage.style.zIndex = '';
      hoverImage.style.opacity = '';
    }

    if (typeof window.resetProductCardSlideshow === 'function') {
      window.resetProductCardSlideshow(productCard);
    }
  }

  /**
   * Update border class on swatches to show which one is selected
   */
  function updateSwatchBorder(productCard, selectedSwatchLink) {
    // Remove select_border from all swatches in this card
    const allSwatchContainers = productCard.querySelectorAll('.width_swatch');
    allSwatchContainers.forEach(container => {
      container.classList.remove('select_border');
    });
    
    // Add select_border to the selected swatch
    if (selectedSwatchLink) {
      const selectedContainer = selectedSwatchLink.closest('.width_swatch');
      if (selectedContainer) {
        selectedContainer.classList.add('select_border');
      }
    }
  }

  /**
   * Handle swatch link interactions using event delegation
   */
  function handleSwatchInteraction(e) {
    const link = e.target.closest('.product-card__swatch-link[data-swatch-image-id]');
    if (!link) return;

    const productCard = link.closest('product-card');
    if (!productCard) return;

    // Initialize card if not already initialized
    initializeCard(productCard);

    const swatchImageId = link.getAttribute('data-swatch-image-id');
    const productUrl = link.getAttribute('data-product-url');

    // Skip if swatch image ID is invalid
    if (!swatchImageId || swatchImageId === 'swatch-image-' || !swatchImageId.startsWith('swatch-image-')) {
      return;
    }

    const mediaContainer = productCard.querySelector('.product-card__media');
    if (!mediaContainer) return;

    // Check if this is the ORIGINAL default swatch (not the currently selected one)
    // We compare against the stored original default, not the current select_border class
    // because we dynamically change select_border when swatches are clicked
    const originalDefaultSwatch = originalDefaultSwatchLinks.get(productCard);
    const isDefaultSwatch = originalDefaultSwatch && originalDefaultSwatch === link;

    const swatchImage = mediaContainer.querySelector(`[data-swatch-image-id="${swatchImageId}"]`);
    
    // For mobile clicks on default swatch, handle it even if no swatch image exists
    if (e.type === 'click' || e.type === 'touchend') {
      if (isMobile() && isDefaultSwatch) {
        // Always prevent navigation on mobile swatch clicks
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        const productCardLinks = productCard.querySelectorAll('.product-card__link[data-product-link]');
        const originalUrl = originalProductUrls.get(productCard);
        
        // Reset links back to original URL
        if (productCardLinks.length > 0 && originalUrl) {
          productCardLinks.forEach(link => {
            link.href = originalUrl;
          });
        }
        
        // Hide all swatch images to show the default/primary image
        const swatchImages = mediaContainer.querySelectorAll('.product-card__swatch-image');
        const hoverImage = mediaContainer.querySelector('.hover-img');
        
        swatchImages.forEach(img => {
          img.style.opacity = '0';
          img.style.zIndex = '0';
        });

        elevateSwatchLayer(productCard, false);
        
        // Reset hover-img so default image can show
        if (hoverImage) {
          hoverImage.style.zIndex = '';
          hoverImage.style.opacity = '';
        }
        
        // Update border to show default swatch is selected
        updateSwatchBorder(productCard, link);

        if (typeof window.resetProductCardSlideshow === 'function') {
          window.resetProductCardSlideshow(productCard);
        }

        return; // Exit early for default swatch on mobile
      }
    }

    // For non-default swatches or desktop, we need the swatch image
    if (!swatchImage) {
      return;
    }

    // Handle different event types
    if (e.type === 'mouseenter') {
      // Desktop hover behavior
      if (!isMobile()) {
        showSwatchImage(productCard, swatchImage, null);
      }
    } else if (e.type === 'mouseleave') {
      // Desktop hover leave behavior
      if (!isMobile()) {
        hideSwatchImage(productCard, swatchImage);
      }
    } else if (e.type === 'click' || e.type === 'touchend') {
      // Mobile click/touch behavior for non-default swatches
      if (isMobile()) {
        // Always prevent navigation on mobile swatch clicks
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Show the swatch image
        showSwatchImage(productCard, swatchImage, productUrl);
        
        // Update border to show this swatch is selected
        updateSwatchBorder(productCard, link);
      }
    }
  }

  /**
   * Initialize all product cards with swatches on page load
   */
  function initializeAllCards() {
    const productCards = document.querySelectorAll('product-card');
    productCards.forEach(card => {
      const swatchLinks = card.querySelectorAll('.product-card__swatch-link[data-swatch-image-id]');
      if (swatchLinks.length > 0) {
        initializeCard(card);
      }
    });
  }

  // Use event delegation for all swatch interactions
  // Single listeners on document instead of per-card listeners
  // These work immediately and will catch events even before cards are initialized
  document.addEventListener('mouseenter', handleSwatchInteraction, true);
  document.addEventListener('mouseleave', handleSwatchInteraction, true);
  document.addEventListener('click', handleSwatchInteraction, true);
  
  // Handle touch events on mobile (touchstart/touchend fire before click)
  // This ensures we prevent navigation on mobile before the click event
  document.addEventListener('touchstart', function(e) {
    const link = e.target.closest('.product-card__swatch-link[data-swatch-image-id]');
    if (link && isMobile()) {
      // Prevent default on touchstart to stop navigation early
      e.preventDefault();
    }
  }, { passive: false, capture: true });
  
  document.addEventListener('touchend', handleSwatchInteraction, { passive: false, capture: true });

  /**
   * Setup MutationObserver to watch for dynamically added cards
   */
  function setupCardObserver() {
    // Ensure document.body exists before observing
    if (!document.body) {
      return;
    }

    // Handle dynamically added cards (e.g., infinite scroll, load more, AJAX sections)
    const cardObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the added node is a product card or contains product cards
            const productCards = node.matches && node.matches('product-card')
              ? [node]
              : node.querySelectorAll && node.querySelectorAll('product-card');

            if (productCards) {
              Array.from(productCards).forEach(card => {
                const swatchLinks = card.querySelectorAll('.product-card__swatch-link[data-swatch-image-id]');
                if (swatchLinks.length > 0) {
                  initializeCard(card);
                }
              });
            }
          }
        });
      });
    });

    // Observe the document body for new product cards
    cardObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize everything when DOM is ready
  function initialize() {
    // Initialize existing cards
    initializeAllCards();
    
    // Setup observer for future cards
    setupCardObserver();
  }

  // Wait for DOM to be ready before initializing
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    // DOM is already ready, but use setTimeout to ensure all sections are rendered
    // This handles cases where script loads before all sections are in the DOM
    setTimeout(initialize, 0);
  }
})();
